import { useEffect, useState, type ReactNode } from "react";
import { getEventBySlug, upcomingEvents, type EventItem } from "./data";
import { BrandMark } from "./components/BrandMark";
import { ArrowIcon } from "./components/ArrowIcon";
import { AudienceForm } from "./components/AudienceForm";
import { EventModal } from "./components/EventModal";
import { CookieConsent } from "./components/CookieConsent";
import { LegalPage } from "./components/LegalPage";
import { StaffCheckIn } from "./components/StaffCheckIn";
import { formatEventDate, useLanguage, type Language } from "./i18n";

function Multiline({ children }: { children: string }) {
  return children.split("\n").map((line, index) => (
    <span key={`${line}-${index}`}>
      {index > 0 && <br />}
      {line}
    </span>
  ));
}

function LanguageSwitch({ language, onChange }: { language: Language; onChange: (language: Language) => void }) {
  return (
    <div className="language-switch" aria-label="Sprache / Language">
      <button type="button" className={language === "de" ? "is-active" : ""} aria-pressed={language === "de"} onClick={() => onChange("de")}>DE</button>
      <span aria-hidden="true">/</span>
      <button type="button" className={language === "en" ? "is-active" : ""} aria-pressed={language === "en"} onClick={() => onChange("en")}>EN</button>
    </div>
  );
}

function HomePage({ onRegister }: { onRegister: (event: EventItem) => void }) {
  const { language, t } = useLanguage();
  const line = (content: string): ReactNode => <Multiline>{content}</Multiline>;

  return (
    <main>
      <section className="hero" aria-labelledby="hero-title">
        <div className="hero-orbit hero-orbit--one" /><div className="hero-orbit hero-orbit--two" />
        <div className="hero-grain" />
        <div className="hero-copy">
          <p className="eyebrow">{t.hero.eyebrow}</p>
          <h1 id="hero-title"><span>{t.hero.rhythm}</span><span className="hero-word--outline">{t.hero.connects}</span><span>{t.hero.people}</span></h1>
          <p className="hero-intro">{t.hero.intro}</p>
          <div className="hero-actions">
            <a className="button button--pink" href="#events">{t.hero.explore} <ArrowIcon /></a>
            <a className="round-link" href="#family" aria-label={t.form.join}><Multiline>{t.hero.join}</Multiline></a>
          </div>
        </div>
        <div className="hero-sidecopy" aria-hidden="true"><span>VIBES</span><span>RHYTHM</span><span>CONNECTION</span></div>
        <p className="hero-script" aria-hidden="true">{t.hero.script}</p>
      </section>

      <section className="manifesto-strip" aria-label="AXON manifesto">
        <div>{t.ticker}</div><div aria-hidden="true">{t.ticker}</div>
      </section>

      <section className="events-section section" id="events">
        <div className="section-heading">
          <div><p className="eyebrow">{t.events.eyebrow}</p><h2>{line(t.events.title)}</h2></div>
          <p>{t.events.intro}</p>
        </div>
        <div className="event-list">
          {upcomingEvents.map((event) => (
            <article className="event-card" key={event.slug}>
              <div className="event-visual">
                <img src={event.flyer} alt={`${t.events.flyerAlt} ${event.title}`} />
                <span className="event-badge">{event.tags[0]}</span>
              </div>
              <div className="event-info">
                <div className="event-date"><span>{formatEventDate(event.date, language)}</span><span>{event.time}</span></div>
                <div><p className="eyebrow">{event.format}</p><h3>{event.title}</h3><p>{event.venue} · {event.city}<br /><span>{event.address}</span></p></div>
                <div className="lineup"><span>{t.events.with}</span><p>{event.artists.join(" · ")}</p></div>
                <div className="event-actions">
                  <a className="button button--outline-dark" href={`/events/${event.slug}`}>{t.events.details} <ArrowIcon /></a>
                  <button className="button button--dark" type="button" onClick={() => onRegister(event)}>{t.events.register} <ArrowIcon /></button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="about-section section" id="about">
        <div className="about-art" aria-hidden="true"><span>AX</span><span>ON</span><i>↗</i></div>
        <div className="about-copy">
          <p className="eyebrow">{t.about.eyebrow}</p>
          <h2>{line(t.about.title)}</h2>
          <p>{t.about.body}</p>
          <a className="inline-link" href="https://www.instagram.com/axon.vibes/" target="_blank" rel="noreferrer">{t.about.follow} <ArrowIcon /></a>
        </div>
      </section>

      <section className="family-section section" id="family">
        <div className="family-heading"><p className="eyebrow">{t.family.eyebrow}</p><h2>Join the<br /><em>family.</em></h2></div>
        <div className="family-form"><p>{t.family.intro}</p><AudienceForm kind="family" /></div>
      </section>

      <section className="contact-section section" id="contact">
        <div><p className="eyebrow">{t.contact.eyebrow}</p><h2>{line(t.contact.title)}</h2><p className="contact-email">hello@axon-events.com</p></div>
        <AudienceForm kind="contact" />
      </section>
    </main>
  );
}

function EventDetailPage({ event, onRegister }: { event: EventItem; onRegister: () => void }) {
  const { language, t } = useLanguage();
  const [shared, setShared] = useState(false);
  const price = (amount: number) => new Intl.NumberFormat(language === "de" ? "de-DE" : "en-IE", { style: "currency", currency: event.admission.currency, maximumFractionDigits: 0 }).format(amount);

  async function shareEvent() {
    const url = window.location.href;
    const shareData = { title: `${event.title} — AXON`, text: event.description[language], url };
    try {
      if (navigator.share) await navigator.share(shareData);
      else {
        await navigator.clipboard.writeText(url);
        setShared(true);
        window.setTimeout(() => setShared(false), 2200);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
    }
  }

  return (
    <main className="event-detail-page">
      <section className="event-detail-hero">
        <div className="event-detail-copy">
          <a className="event-back" href="/#events">← {t.eventPage.back}</a>
          <div className="event-tags">{event.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          <p className="eyebrow">{formatEventDate(event.date, language)}</p>
          <h1>{event.title}</h1>
          <p className="event-detail-intro">{event.description[language]}</p>
          <div className="event-detail-actions">
            <button className="button button--pink" type="button" onClick={onRegister}>{t.events.register} <ArrowIcon /></button>
            <button className="button button--outline-light" type="button" onClick={shareEvent}>{shared ? t.eventPage.copied : t.eventPage.share}</button>
          </div>
        </div>
        <div className="event-detail-poster"><img src={event.flyer} alt={`${t.events.flyerAlt} ${event.title}`} /></div>
      </section>

      <section className="event-overview section">
        <div className="event-overview-heading"><p className="eyebrow">{t.eventPage.overview}</p><h2>{event.tags[0]}<br /><span>{event.city}</span></h2></div>
        <div className="event-facts">
          <article><span>01</span><p>{t.eventPage.dateTime}</p><strong>{formatEventDate(event.date, language)}<br />{event.time}</strong></article>
          <article><span>02</span><p>{t.eventPage.location}</p><strong>{event.venue}<br />{event.address}</strong></article>
          <article><span>03</span><p>{t.eventPage.lineup}</p><strong>{event.artists.join(" · ")}</strong></article>
        </div>
      </section>

      <section className="admission-section section">
        <div className="admission-heading"><p className="eyebrow">{t.eventPage.admission}</p><h2>{price(event.admission.registeredPrice)} <span>/</span> {price(event.admission.doorPrice)}</h2><p>{t.eventPage.registrationNote}</p></div>
        <div className="price-grid">
          <article className="price-card price-card--featured"><span>{t.eventPage.registeredPrice}</span><strong>{price(event.admission.registeredPrice)}</strong><button className="button button--light" type="button" onClick={onRegister}>{t.events.register} <ArrowIcon /></button></article>
          <article className="price-card"><span>{t.eventPage.doorPrice}</span><strong>{price(event.admission.doorPrice)}</strong><small>{t.eventPage.paymentDetail}</small></article>
        </div>
        <div className="event-policies">
          <article><p>{t.eventPage.payment}</p><strong>{t.eventPage.paymentDetail}</strong></article>
          <article><p>{t.eventPage.confirmation}</p><strong>{t.eventPage.confirmationDetail}</strong></article>
          <article><p>{t.eventPage.drinks}</p><strong>{t.eventPage.drinksDetail}</strong></article>
          <article><p>{t.eventPage.minimumSpend}</p><strong>✓</strong></article>
        </div>
      </section>
    </main>
  );
}

function App() {
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const { language, setLanguage, t } = useLanguage();
  const routeMatch = window.location.pathname.match(/^\/events\/([^/]+)\/?$/);
  const routeEvent = routeMatch ? getEventBySlug(decodeURIComponent(routeMatch[1])) : undefined;
  const isEventPage = Boolean(routeEvent);
  const legalRoute = window.location.pathname.replace(/\/$/, "") === "/impressum" ? "imprint" : window.location.pathname.replace(/\/$/, "") === "/datenschutz" ? "privacy" : null;
  const isStaffPage = window.location.pathname.replace(/\/$/, "") === "/staff/check-in";
  const isStandalonePage = isEventPage || Boolean(legalRoute);
  const sectionHref = (section: string) => isStandalonePage ? `/#${section}` : `#${section}`;

  useEffect(() => {
    document.title = routeEvent ? `${routeEvent.title} — AXON` : legalRoute === "imprint" ? `${t.footer.imprint} — AXON` : legalRoute === "privacy" ? `${t.footer.privacy} — AXON` : "AXON — Rhythm connects us";
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = routeEvent ? `https://axon-events.com/events/${routeEvent.slug}` : legalRoute ? `https://axon-events.com/${legalRoute === "imprint" ? "impressum" : "datenschutz"}` : "https://axon-events.com/";
  }, [routeEvent, legalRoute, language, t.footer.imprint, t.footer.privacy]);

  if (isStaffPage) return <StaffCheckIn />;

  return (
    <>
      <header className={`site-header${legalRoute ? " site-header--legal" : ""}`} id="top">
        <BrandMark />
        <nav aria-label={language === "de" ? "Hauptnavigation" : "Main navigation"}>
          <a href={sectionHref("events")}>{t.nav.events}</a><a href={sectionHref("about")}>{t.nav.about}</a><a href={sectionHref("family")}>{t.nav.family}</a><a href={sectionHref("contact")}>{t.nav.contact}</a>
        </nav>
        <div className="header-tools">
          <LanguageSwitch language={language} onChange={setLanguage} />
          {routeEvent ? (
            <button className="header-cta header-cta--button" type="button" onClick={() => setSelectedEvent(routeEvent)}>{t.events.register} <ArrowIcon /></button>
          ) : (
            <a className="header-cta" href={isStandalonePage ? "/#events" : "#events"}>{t.nav.next} <ArrowIcon /></a>
          )}
        </div>
      </header>

      {routeEvent ? <EventDetailPage event={routeEvent} onRegister={() => setSelectedEvent(routeEvent)} /> : legalRoute ? <LegalPage kind={legalRoute} /> : <HomePage onRegister={setSelectedEvent} />}

      <footer className="site-footer">
        <BrandMark compact />
        <p>{t.footer.identity}</p>
        <div className="footer-links"><a href="/impressum">{t.footer.imprint}</a><a href="/datenschutz">{t.footer.privacy}</a><a href="https://www.instagram.com/axon.vibes/" target="_blank" rel="noreferrer">Instagram</a></div>
      </footer>

      <EventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      <CookieConsent />
    </>
  );
}

export default App;
